/**
 * Scrub (hover/touch-drag) state for elevation-panel-v2.
 *
 * Deliberately thin: `ElevationPanelChart.vue` already resolves pointer AND
 * touch input to a nearest-sample `index` via Chart.js's own `interaction:
 * {mode: 'index', intersect: false}` handling (Chart.js's default `events`
 * list includes `touchstart`/`touchmove`, so touch-drag scrub on mobile and
 * mouse-hover scrub on desktop both flow through the same `hover`/`hoverEnd`
 * events without this composable needing to reimplement pointer math Chart.js
 * already does well). This composable just holds the resulting `activeIndex`
 * for the container to read (and to resolve into a map marker / readout).
 *
 * Also drives keyboard scrub: `ElevationPanelChart.vue`'s own `ArrowLeft`/
 * `ArrowRight`/`Home`/`End`/`Escape` handling emits through this exact same
 * `hover`/`hoverEnd` path, so there is only one code path to keep correct,
 * not a parallel accessibility-only implementation.
 */
import { ref, type Ref } from 'vue';

export interface UseElevationScrub {
  activeIndex: Ref<number | null>;
  setActiveIndex(index: number): void;
  clear(): void;
}

export function useElevationScrub(): UseElevationScrub {
  const activeIndex = ref<number | null>(null);

  function setActiveIndex(index: number): void {
    activeIndex.value = index;
  }

  function clear(): void {
    activeIndex.value = null;
  }

  return { activeIndex, setActiveIndex, clear };
}
